import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  LogOut, Plus, Package, MessageCircle, Clock, CheckCircle2, XCircle,
  Eye, Sparkles, ArrowLeft, Loader2, Send, Calendar, CreditCard, FileText, Download
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SkeletonList } from "@/components/admin/Skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new:         { label: "New",          color: "text-blue-400 bg-blue-600/10 border-blue-600/20",       Icon: Sparkles },
  in_review:   { label: "In Review",    color: "text-yellow-400 bg-yellow-600/10 border-yellow-600/20", Icon: Eye },
  approved:    { label: "Approved",     color: "text-green-400 bg-green-600/10 border-green-600/20",    Icon: CheckCircle2 },
  in_progress: { label: "In Progress",  color: "text-purple-400 bg-purple-600/10 border-purple-600/20", Icon: Clock },
  completed:   { label: "Completed",    color: "text-emerald-400 bg-emerald-600/10 border-emerald-600/20", Icon: CheckCircle2 },
  rejected:    { label: "Rejected",     color: "text-red-400 bg-red-600/10 border-red-600/20",          Icon: XCircle },
};

const BUSINESS_TYPES = [
  "Restaurant / Cafe", "Gym / Fitness", "Salon / Spa", "Medical / Clinic",
  "Law Firm", "Real Estate", "School / Coaching", "Photography",
  "Hotel / Resort", "Travel / Tourism", "Retail / Shop", "E-Commerce",
  "Freelancer / Portfolio", "NGO / Non-Profit", "Other"
];

const clientAuthHeaders = () => {
  const token = localStorage.getItem("koko_client_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [downloadingInv, setDownloadingInv] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/mine`, { headers: clientAuthHeaders() });
      setOrders(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("koko_client_token");
        localStorage.removeItem("koko_client_user");
        navigate("/login", { replace: true });
      } else {
        toast.error("Failed to load orders");
      }
    }
    setLoading(false);
  }, [navigate]);

  const fetchInvoices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/client/invoices`, { headers: clientAuthHeaders() });
      setInvoices(data || []);
    } catch (_) { /* silent — invoices are optional */ }
  }, []);

  const downloadInvoice = async (number) => {
    setDownloadingInv(number);
    try {
      const res = await axios.get(`${API}/client/invoices/${number}/pdf`, {
        headers: clientAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingInv(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("koko_client_token");
    const stored = localStorage.getItem("koko_client_user");
    if (!token || !stored) {
      navigate("/login", { replace: true });
      return;
    }
    setUser(JSON.parse(stored));
    fetchOrders();
    fetchInvoices();
  }, [fetchOrders, fetchInvoices, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("koko_client_token");
    localStorage.removeItem("koko_client_refresh");
    localStorage.removeItem("koko_client_user");
    toast.success("Logged out");
    navigate("/", { replace: true });
  };

  if (!user) return null;

  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Helmet>
        <title>My Dashboard — KoKo Sites</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center font-black text-white text-sm shrink-0">
              {user.name?.[0]?.toUpperCase() || "C"}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">My Dashboard</p>
              <h1 className="text-sm sm:text-base font-bold text-white truncate" data-testid="client-dashboard-name">{user.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="client-back-home" className="tap hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors">
              <ArrowLeft size={12} /> Home
            </Link>
            <button onClick={handleLogout} data-testid="client-logout" className="tap inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-red-500 hover:text-white border border-red-600/60 hover:border-red-600 hover:bg-red-600/10 px-3 py-2 transition-colors">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-[100dvh] bg-[#0A0A0A] pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
          {/* Welcome + New Order */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white">
                Hello, <span className="text-red-600">{user.name?.split(" ")[0]}</span>
              </h2>
              <p className="text-sm text-zinc-500 mt-2">
                {orders.length === 0
                  ? "Place your first order — we'll get you a quote within 24 hours."
                  : `You have ${orders.length} order${orders.length === 1 ? "" : "s"}. Track their progress below.`}
              </p>
            </div>
            <button
              onClick={() => setShowNewOrder(true)}
              data-testid="new-order-btn"
              className="btn-brutal btn-shine inline-flex items-center justify-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white hover:bg-white hover:text-black text-sm"
            >
              <Plus size={16} /> New Order
            </button>
          </div>

          {/* Status summary */}
          {orders.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-8">
              {Object.entries(STATUS_META).map(([key, meta]) => {
                const count = statusCounts[key] || 0;
                const MetaIcon = meta.Icon;
                return (
                  <div key={key} className={`border p-3 ${meta.color}`}>
                    <div className="flex items-center gap-1.5 mb-1"><MetaIcon size={11} /><span className="text-[9px] uppercase tracking-widest">{meta.label}</span></div>
                    <p className="text-2xl font-black leading-none">{count}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Orders list */}
          <div className="space-y-3">
            {loading ? (
              <SkeletonList count={3} />
            ) : orders.length === 0 ? (
              <div className="border border-dashed border-white/10 bg-[#111] p-12 text-center">
                <Package size={36} className="text-zinc-700 mx-auto mb-4" />
                <p className="text-white font-bold uppercase tracking-wider text-sm mb-2">No orders yet</p>
                <p className="text-xs text-zinc-500 mb-6">Place your first order and we'll get back to you with a quote.</p>
                <button onClick={() => setShowNewOrder(true)} className="btn-brutal btn-shine inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white text-xs">
                  <Plus size={14} /> Place Your First Order
                </button>
              </div>
            ) : (
              orders.map((order) => {
                const meta = STATUS_META[order.status] || STATUS_META.new;
                const MetaIcon = meta.Icon;
                return (
                  <button
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    data-testid={`order-row-${order.id}`}
                    className="tap-soft w-full text-left border border-white/5 bg-[#111] hover:border-red-600/40 hover:bg-[#151515] p-5 transition-all group"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 ${meta.color}`}>
                          <MetaIcon size={10} /> {meta.label}
                        </span>
                        <span className="text-[10px] text-zinc-600 uppercase tracking-wider">
                          {new Date(order.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                      </div>
                      {order.quoted_amount != null && (
                        <span className="text-sm font-black text-red-500">Rs {Number(order.quoted_amount).toLocaleString("en-IN")}</span>
                      )}
                    </div>
                    <h3 className="text-base sm:text-lg font-black uppercase text-white mb-1 group-hover:text-red-500 transition-colors">{order.title}</h3>
                    <p className="text-xs text-zinc-500 line-clamp-2">{order.description}</p>
                    {order.messages?.length > 0 && (
                      <div className="mt-3 flex items-center gap-1.5 text-[10px] text-zinc-500">
                        <MessageCircle size={11} className="text-red-500" />
                        <span className="uppercase tracking-wider">{order.messages.length} message{order.messages.length === 1 ? "" : "s"}</span>
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Invoices */}
          {invoices.length > 0 && (
            <div className="mt-10" data-testid="client-invoices">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm sm:text-base font-black uppercase tracking-widest text-white flex items-center gap-2">
                  <FileText size={14} className="text-red-500" /> Invoices
                </h2>
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  {invoices.length} issued
                </span>
              </div>
              <div className="grid gap-2">
                {invoices.map((inv) => {
                  const isProforma = inv.kind === "proforma";
                  const isBalance = inv.kind === "balance";
                  const isDue = isProforma || isBalance;
                  return (
                    <div
                      key={inv.number}
                      data-testid={`invoice-row-${inv.number}`}
                      className={`border ${isProforma ? "border-amber-600/30 bg-amber-600/5" : isBalance ? "border-emerald-600/30 bg-emerald-600/5" : "border-white/5 bg-[#111]"} hover:border-red-600/40 hover:bg-[#151515] p-4 transition-all flex items-center justify-between gap-3 flex-wrap`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-sm font-black text-white font-mono">{inv.number}</span>
                          {isProforma ? (
                            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-amber-600/20 text-amber-300 border border-amber-600/40">DUE · ADVANCE</span>
                          ) : isBalance ? (
                            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-emerald-600/20 text-emerald-300 border border-emerald-600/40">BALANCE DUE</span>
                          ) : (
                            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-emerald-600/20 text-emerald-400 border border-emerald-600/40">PAID</span>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-500 truncate">
                          {inv.issued_at_str} · {inv.project_name}
                          {isDue && inv.due_date_str ? ` · Due by ${inv.due_date_str}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`text-sm font-black ${isProforma ? "text-amber-300" : isBalance ? "text-emerald-300" : "text-red-500"}`}>
                          Rs {Number(inv.amount).toLocaleString("en-IN")}
                        </span>
                        <button
                          onClick={() => downloadInvoice(inv.number)}
                          disabled={downloadingInv === inv.number}
                          data-testid={`invoice-download-${inv.number}`}
                          className="tap inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white bg-red-600 hover:bg-white hover:text-black border border-white px-3 py-2 disabled:opacity-60"
                        >
                          {downloadingInv === inv.number ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                          PDF
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {showNewOrder && (
        <NewOrderModal
          onClose={() => setShowNewOrder(false)}
          onCreated={(newOrder) => {
            setOrders(prev => [newOrder, ...prev]);
            setShowNewOrder(false);
            toast.success("Order placed! We'll review and respond within 24 hours.");
          }}
        />
      )}

      {selectedOrder && (
        <OrderDetailModal
          orderId={selectedOrder.id}
          onClose={() => setSelectedOrder(null)}
          onUpdate={(updated) => setOrders(prev => prev.map(o => o.id === updated.id ? updated : o))}
        />
      )}
    </>
  );
}

// =============== New Order Modal ===============
function NewOrderModal({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", budget: "", timeline: "",
    business_type: "", reference_urls: "",
  });

  useEffect(() => {
    document.body.classList.add("no-scroll");
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, budget: form.budget ? parseInt(form.budget) : null };
      const { data } = await axios.post(`${API}/orders`, payload, { headers: clientAuthHeaders() });
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
    }
    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 animate-fadeIn"
      data-testid="new-order-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto relative">
        <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-black uppercase tracking-tight">New Order</h2>
          <button onClick={onClose} className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-white" aria-label="Close">
            <XCircle size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Project Title *</label>
            <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Website for my dental clinic"
              data-testid="order-title"
              className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Tell us what you need *</label>
            <textarea required rows={5} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="What kind of site? What pages do you need? Any specific features? Share as much detail as possible."
              data-testid="order-description"
              className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none resize-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Business Type</label>
            <select value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none">
              <option value="">Select...</option>
              {BUSINESS_TYPES.map(bt => <option key={bt} value={bt}>{bt}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><CreditCard size={11} /> Budget (Rs)</label>
              <input value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })}
                type="number" inputMode="numeric" min="0"
                placeholder="e.g. 30000"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><Calendar size={11} /> Timeline</label>
              <input value={form.timeline} onChange={e => setForm({ ...form, timeline: e.target.value })}
                placeholder="e.g. 2 weeks, ASAP"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Reference Websites <span className="text-zinc-700 normal-case">(optional)</span></label>
            <input value={form.reference_urls} onChange={e => setForm({ ...form, reference_urls: e.target.value })}
              placeholder="Any sites you love? Paste URLs."
              className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none" />
          </div>

          <button type="submit" disabled={loading} data-testid="order-submit"
            className="btn-brutal btn-shine w-full bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-6 border-2 border-white hover:bg-white hover:text-black text-sm disabled:opacity-60 flex items-center justify-center gap-2 mt-4">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Placing Order...</>
              : <><Send size={14} /> Place Order</>}
          </button>
        </form>
      </div>
    </div>
  );
}

// =============== Order Detail Modal ===============
function OrderDetailModal({ orderId, onClose, onUpdate }) {
  const [order, setOrder] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}`, { headers: clientAuthHeaders() });
      setOrder(data);
      onUpdate(data);
    } catch (err) {
      toast.error("Failed to load order");
      onClose();
    }
  }, [orderId, onUpdate, onClose]);

  useEffect(() => {
    document.body.classList.add("no-scroll");
    fetchOrder();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [fetchOrder, onClose]);

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/orders/${orderId}/message`, { message: msg }, { headers: clientAuthHeaders() });
      setMsg("");
      await fetchOrder();
    } catch {
      toast.error("Failed to send");
    }
    setSending(false);
  };

  if (!order) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={onClose}>
        <Loader2 size={28} className="text-red-500 animate-spin" />
      </div>
    );
  }

  const meta = STATUS_META[order.status] || STATUS_META.new;
  const MetaIcon = meta.Icon;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 animate-fadeIn"
      data-testid="order-detail-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto relative">
        <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 shrink-0 ${meta.color}`}>
              <MetaIcon size={10} /> {meta.label}
            </span>
            <h2 className="text-sm sm:text-base font-black uppercase truncate">{order.title}</h2>
          </div>
          <button onClick={onClose} className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-white" aria-label="Close">
            <XCircle size={22} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="border border-white/10 bg-[#111] p-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Placed</p>
              <p className="text-white font-bold">{new Date(order.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
            </div>
            {order.quoted_amount != null ? (
              <div className="border border-red-600/30 bg-red-600/5 p-3">
                <p className="text-[10px] uppercase tracking-widest text-red-500 mb-1">Quoted</p>
                <p className="text-white font-black text-lg">Rs {Number(order.quoted_amount).toLocaleString("en-IN")}</p>
              </div>
            ) : (
              <div className="border border-white/10 bg-[#111] p-3">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Budget</p>
                <p className="text-white font-bold">{order.budget ? `Rs ${Number(order.budget).toLocaleString("en-IN")}` : "Not set"}</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Your Brief</p>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{order.description}</p>
          </div>

          {order.admin_note && (
            <div className="border-l-2 border-red-600 bg-red-600/5 pl-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-red-500 mb-1">Note from KoKo Sites</p>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{order.admin_note}</p>
            </div>
          )}

          {/* Messages thread */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
              <MessageCircle size={11} /> Conversation ({order.messages?.length || 0})
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1 mb-4">
              {(order.messages || []).length === 0 && (
                <p className="text-xs text-zinc-600 italic">No messages yet. Send one below to start the conversation.</p>
              )}
              {(order.messages || []).map(m => (
                <div key={m.id} className={`border p-3 ${m.sender === "client" ? "border-blue-600/20 bg-blue-600/5 ml-0 mr-8" : "border-red-600/20 bg-red-600/5 ml-8 mr-0"}`}>
                  <p className="text-[10px] uppercase tracking-widest mb-1 text-zinc-500">{m.sender_name} <span className="text-zinc-600">· {new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span></p>
                  <p className="text-sm text-white whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
            <form onSubmit={sendMsg} className="flex gap-2">
              <input value={msg} onChange={e => setMsg(e.target.value)}
                placeholder="Type a message..."
                enterKeyHint="send"
                data-testid="order-msg-input"
                className="flex-1 bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none" />
              <button type="submit" disabled={sending || !msg.trim()} data-testid="order-msg-send"
                className="tap bg-red-600 text-white px-4 py-3 border-2 border-white disabled:opacity-50 flex items-center justify-center">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
